<script setup lang="ts">
import type { ViewModeOptionType } from '#leaderboards/store/useLeaderboardStore'
import type { ValueFormat } from '@forgd/formatter'
import { useLeaderboardStore } from '#leaderboards/store/useLeaderboardStore'
import { formatValue } from '@forgd/formatter'

interface CellType {
  value: number | null
  percentile: number
  rank: number
}

const props = defineProps<{
  valueCell: CellType
  marketPercentageCell?: CellType
  valueFormat: ValueFormat
  viewMode?: ViewModeOptionType
}>()

const store = useLeaderboardStore()

const viewMode = computed(() => {
  return props.viewMode ?? store.viewMode
})

const hasNoValue = computed(() => props.valueCell.value === null)

const noValueLabel = computed(() => {
  if (!hasNoValue.value) {
    return null
  }
  return props.marketPercentageCell ? 'Data Not Available' : 'N/A'
})

const heatmapColorSchema = 'bg-green-heatmap'

const value = computed(() => {
  function getFormattedValue() {
    switch (viewMode.value) {
      case 'value':
        return formatValue(props.valueCell.value, props.valueFormat)
      case 'percentile':
        return formatValue(props.valueCell.percentile, 'percent')
      case 'rank':
        return `${props.valueCell.rank}`
      default:
        return ''
    }
  }

  const formattedValue = getFormattedValue()
  const hasMarketPercentage = !!props.marketPercentageCell

  switch (viewMode.value) {
    case 'value':
      return formattedValue
    case 'percentile':
      return hasMarketPercentage ? `Value: ${formattedValue}` : formattedValue
    case 'rank':
      return hasMarketPercentage ? `Value Rank: ${formattedValue}` : `Rank: ${formattedValue}`
    default:
      return ''
  }
})

const marketPercentage = computed(() => {
  if (!props.marketPercentageCell) {
    return null
  }

  switch (viewMode.value) {
    case 'value':
      return `${formatValue(props.marketPercentageCell?.value, 'percent:2')} of Market`
    case 'percentile':
      return `% of Market: ${formatValue(props.marketPercentageCell?.percentile, 'percent')}`
    case 'rank':
      return `% of Market: ${props.marketPercentageCell?.rank}`
    default:
      return null
  }
})

function rankToHeatmapPercent(rank: number) {
  if (!Number.isFinite(rank) || rank <= 0) {
    return 0
  }

  const percent = 100 - (rank - 1) * 20
  // Clamp to [0, 100]
  return Math.min(Math.max(percent, 0), 100)
}

const heatmapColor = computed(() => {
  if (viewMode.value === 'value') {
    return null
  }

  const value = viewMode.value === 'percentile' ? props.valueCell.percentile : rankToHeatmapPercent(props.valueCell.rank)
  const percent = Math.min(Math.max(value, 0), 100)
  const claspedPercent = Math.floor(percent / 10) * 10

  return `${heatmapColorSchema}-${claspedPercent}`
})
</script>

<template>
  <div class="flex h-full w-full items-center px-2 py-1" :class="heatmapColor">
    <div class="flex flex-col">
      <span
        class="text-numeric text-mono-sm"
        :class="{ 'text-disabled/30': hasNoValue }"
      >{{ noValueLabel ?? value }}</span>
      <span v-if="marketPercentage && !hasNoValue" class="text-2xs text-placeholder font-display">{{ marketPercentage }}</span>
    </div>
  </div>
</template>
